import React from 'react';
import { observer } from 'mobx-react';
import { AppUI, ExerciseUI } from '@seedlang/state';
import Spinner from 'components/spinner';
import styled from '@emotion/styled';
import YoutubeThumbnailPlayer from 'components/worksheet/youtube_thumbnail_player';
import PodcastPlayer from 'components/worksheet/podcast_player';
import Stack, { gaps } from 'components/layout/stack';
import ExerciseNavigation from './exercise_navigation';
import Link from 'components/link';
import WorksheetSubtitleRow from './worksheet_subtitle_row';
import WorksheetTabs from './worksheet_tabs';
import { LearningPathWorksheetStore } from '@seedlang/stores';
import eventParamsHelper from 'utils/event_params_helper';

const Wrapper = styled.div`
  padding: ${gaps.md};
  background: white;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: ${gaps.md};
  width: 100%;
  animation: slideUp 0.2s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Title = styled(Link)`
  font-size: 28px;
  line-height: 32px;
  font-weight: 900;

  &:hover {
    text-decoration: underline;
  }
`;

const PodcastEpisodeSubtitle = styled.div`
  color: #666;
  font-size: 15px;
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: ${gaps.md} 0;
`;

const AnimatedHeightWrapper = styled.div`
  overflow: hidden;
  transition: ${props => props.animate ? 'height 0.25s ease' : 'none'};
`;

const AnimatedHeightContent = styled.div`
  width: 100%;
`;

class AnimatedHeight extends React.Component {
  state = {
    animate: false,
    height: null,
  };

  componentDidMount() {
    this.measure();

    if (window.ResizeObserver && this.contentRef) {
      this.resizeObserver = new window.ResizeObserver(this.measure);
      this.resizeObserver.observe(this.contentRef);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.animationKey !== this.props.animationKey && !this.state.animate) {
      this.setState({ animate: true }, this.measure);
      return;
    }

    this.measure();
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  setContentRef = element => {
    this.contentRef = element;
  }

  measure = () => {
    if (!this.contentRef) return;

    const height = this.contentRef.getBoundingClientRect().height;
    if (Math.abs(height - (this.state.height || 0)) > 0.5) {
      this.setState({ height });
    }
  }

  render() {
    return (
      <AnimatedHeightWrapper
        animate={this.state.animate}
        style={{ height: this.state.height === null ? 'auto' : `${this.state.height}px` }}
      >
        <AnimatedHeightContent ref={this.setContentRef}>
          {this.props.children}
        </AnimatedHeightContent>
      </AnimatedHeightWrapper>
    );
  }
}

@observer
class WorksheetPreview extends React.Component {
  handleVideoThumbnailClick = () => {
    if (AppUI.siteIsMemrev) {
      AppUI.createEvent('memrev - click YouTube video', eventParamsHelper({
        worksheet_id: ExerciseUI.worksheet?.id,
        youtube_video_id: ExerciseUI.worksheet?.youtubeVideoId,
      }));
    }
  }

  render() {
    if (ExerciseUI.worksheetStore.hasOutstandingRequests || !ExerciseUI.hasWorksheet) {
      return (
        <Wrapper>
          <Spinner />
        </Wrapper>
      );
    }

    const podcastPreviewIsLoading = LearningPathWorksheetStore.hasOutstandingRequests || (
      ExerciseUI.worksheet.podcastEpisode && !ExerciseUI.worksheet.accessStatus
    );

    return (
      <Wrapper>
        <AnimatedHeight animationKey={ExerciseUI.worksheet.id}>
          {
            ExerciseUI.formattedYoutubeUrl && (
              <YoutubeThumbnailPlayer
                allowFloating={false}
                hasAccess={ExerciseUI.worksheet?.accessStatus?.hasAccess}
                url={ExerciseUI.formattedYoutubeUrl}
                thumbnailUrl={ExerciseUI.worksheet?.imageUrl}
                placeholderImageUrl={ExerciseUI.worksheet?.image?.url}
                podcastEpisode={ExerciseUI.worksheet.podcastEpisode}
                durationSeconds={ExerciseUI.worksheet?.videoDurationSeconds}
                onPlay={this.handleVideoThumbnailClick}
                width='none'
              />
            )
          }
          {
            ExerciseUI.worksheet.podcastEpisode && !ExerciseUI.formattedYoutubeUrl &&
              <PodcastPlayer
                hasAccess={ExerciseUI.worksheet?.accessStatus?.hasAccess}
                noAccessReason={ExerciseUI.worksheet?.accessStatus?.reason}
                imageUrl={ExerciseUI.worksheet.imageUrl}
                loading={podcastPreviewIsLoading}
                podcast={ExerciseUI.worksheet.podcastEpisode.podcast}
                podcastEpisode={ExerciseUI.worksheet.podcastEpisode}
                previewLayout
              />
          }
        </AnimatedHeight>
        <Stack gap='xs' alignItems='stretch'>
          <Title to={{name: 'learn.show', params: {worksheetId: ExerciseUI.worksheet.routeId}}}>
            {ExerciseUI.worksheet.nameWithPublished}
          </Title>
          {
            ExerciseUI.worksheet.podcastEpisode?.subtitle && (
              <PodcastEpisodeSubtitle>
                {ExerciseUI.worksheet.podcastEpisode.subtitle}
              </PodcastEpisodeSubtitle>
            )
          }
          <WorksheetSubtitleRow episodeCode={ExerciseUI.worksheet.episodeCode} />
        </Stack>
        <Stack gap='sm' alignItems='stretch'>
          {
            LearningPathWorksheetStore.hasOutstandingRequests ? (
              <SpinnerWrapper>
                <Spinner />
              </SpinnerWrapper>
            ) : (
              <Stack inline gap='xs'>
                <WorksheetTabs
                  worksheetId={ExerciseUI.worksheet.routeId}
                  params={ExerciseUI.routeStore.params}
                  showCustomTabs={false}
                  small
                  activeColor='#FFF'
                  activeBackground={AppUI.site.accentColor}
                  alwaysShowDiscuss
                  alwaysShowDownloads
                />
              </Stack>
            )
          }
          <ExerciseNavigation
            padding='0'
            exercises={ExerciseUI.worksheetStore.showData.exercises}
            worksheetId={ExerciseUI.worksheet?.routeId}
            useWorksheetRoute
          />
        </Stack>
      </Wrapper>
    );
  }
}

export default WorksheetPreview;
